import React, {FC, useState, useEffect} from "react"

import theme from "@irati/ui/src/global/theme"
import {FormattedMessage} from "@irati/utils/lang"
import Flex from "@irati/ui/src/Flex"
import {H3, Body} from "@irati/ui/src/Typography"
import Button from "@irati/ui/src/Button"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import {getOAuthSettings} from "@irati/sso/services/sso"
import authentication from "@irati/auth"
import Loading from "@irati/ui/src/Loading"
import {OAuthProvider} from "@irati/types"
import workosService from "@irati/workos/services/workos"
import adminTracking from "@irati-tracking/admin"

import {InfoCard} from "../../styled"
import EditOauth from "./EditOauth"
import LoginViaEmailPassword from "./LoginViaEmailPassword"
import {OAuthSettings} from "../EnableSSO/EnableOAuth"

export const EditHeader = () => {
  return (
    <>
      <H3 marginBottom={1}>
        <FormattedMessage id="admin.components.integration.sso.editSSO.start.title" />
      </H3>
      <Body marginBottom={4}>
        <FormattedMessage id="admin.components.integration.sso.editSSO.start.subTitle" />
      </Body>
    </>
  )
}

const SSO_EDIT_START = "SSO_editStart"

interface Props {
  onDelete: () => void
}

const EditSSO: FC<Props> = ({onDelete}) => {
  const [load, setLoad] = useState<Promise<any> | null>(null)
  const [step, setStep] = useState<number | null>(0)
  const [provider, setProvider] = useState<OAuthProvider>()
  const [domains, setDomains] = useState<string[]>()
  const [btnLoad, setBtnLoad] = useState<Promise<any> | null>(null)

  const handleStart = () => {
    adminTracking.editIntegrationSSOJourney(SSO_EDIT_START)
    setStep(1)
  }

  const handleNewOauth = (data?: OAuthSettings) => {
    setProvider(data?.provider)
    setDomains(data?.domains)
    setStep(2)
  }

  const redirectToWorkOS = () => {
    const loaded = workosService.generatePortalLinks(authentication.business().uuid).then((portalLinks) => {
      if (portalLinks?.ssoUrl) {
        window.location = portalLinks.ssoUrl
      }
    })
    setBtnLoad(loaded)
  }

  useEffect(() => {
    const loaded = getOAuthSettings(authentication.business().uuid, {fetchPolicy: "network-only"}).then((data) => {
      if (data.provider === OAuthProvider.Unknown) {
        setStep(3)
      } else {
        setProvider(data.provider)
        setDomains(data.domains)
      }
    }).catch(() => {
      setStep(3)
    })
    setLoad(loaded)
  }, [])

  return (
    <InfoCard padding={4} marginTop={6}>
      <Flex width="100%" direction="column">
        <Loading load={load}>
          {(() => {
            switch (step) {
              case 0:
                return (
                <>
                  <EditHeader />
                  <div>
                    <Button onClick={handleStart} primary>
                      <FormattedMessage id="admin.components.integration.sso.editSSO.start.btn" />
                    </Button>
                  </div>
                </>
                )
              case 1:
                return (
                  provider && domains &&
                  <EditOauth
                    provider={provider}
                    domains={domains}
                    onClose={() => setStep(0)}
                    onSave={(data) => handleNewOauth(data)}
                    onDelete={onDelete} />
                )
              case 2:
                return (
                <>
                  <EditHeader />
                  <Flex direction="column">
                    <InfoCard backgroundColor={theme.colors.greys.light} padding={2} marginBottom={4}>
                      <Icon icon={IconTypes.circleTickFill} marginRight size={20} />
                      <Body color={theme.colors.filterChip}>
                        <FormattedMessage id="admin.components.integration.sso.editSSO.success" />
                      </Body>
                    </InfoCard>
                    <div>
                      <Button onClick={() => setStep(1)} primary>
                        <FormattedMessage id="admin.components.integration.sso.editSSO.start.btn" />
                      </Button>
                    </div>
                  </Flex>
                </>
                )
              case 3:
                return (
                <>
                  <EditHeader />
                  <LoginViaEmailPassword />
                  <div>
                    <Button load={btnLoad} onClick={redirectToWorkOS} primary>
                      <FormattedMessage id="admin.components.integration.sso.editSSO.edit.workOS.btn" />
                    </Button>
                  </div>
                </>
                )
              default:
                return null
            }
          })()}
        </Loading>
      </Flex>
    </InfoCard>
  )
}

export default EditSSO
